import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import Layout from "./components/Layout";
import ProLayout from "./components/ProLayout";
import ConsentModal from "./components/ConsentModal";
import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import HomePage from "./pages/Home";
import ExercisesPage from "./pages/Exercises";
import HealthPage from "./pages/Health";
import CommunicatePage from "./pages/Communicate";
import ProfilePage from "./pages/Profile";
import SubscriptionPage from "./pages/Subscription";
import AvcExplainedPage from "./pages/AvcExplained";
import PaymentSuccessPage, { PaymentCancelPage } from "./pages/Payment";
import PrivacyPage from "./pages/Privacy";
import ProDashboard from "./pages/pro/Dashboard";
import PatientDetail from "./pages/pro/PatientDetail";
import TrainingPage from "./pages/pro/Training";
import ProSettings from "./pages/pro/Settings";
import ReportsPage from "./pages/pro/Reports";
import ClinicPage from "./pages/pro/Clinic";
import InstallGuidePage from "./pages/InstallGuide";
import CaregiverNotebookPage from "./pages/CaregiverNotebook";
import FamilyBoardPage from "./pages/FamilyBoard";
import CaregiverAlertsPage from "./pages/CaregiverAlerts";
import WorryModePage from "./pages/WorryMode";
import TranscriptionsPage from "./pages/pro/Transcriptions";
import LingalaPage from "./pages/pro/Lingala";
import NetworkPage from "./pages/pro/Network";
import LibraryPage from "./pages/pro/Library";
import TransmissionPage from "./pages/pro/Transmission";
import PalabrePage from "./pages/pro/Palabre";
import WorriesPage from "./pages/pro/Worries";
import TelesoinPage from "./pages/pro/Telesoin";
import NomenclaturePage from "./pages/pro/Nomenclature";
import PaysDashboardPage from "./pages/pro/PaysDashboard";
import JeuPage from "./pages/pro/Jeu";
import PatientJeu from "./pages/pro/PatientJeu";
import EtudePage from "./pages/pro/Etude";
import EtudeParticipant from "./pages/pro/EtudeParticipant";
import AvisMedecinPage from "./pages/AvisMedecin";
import VideosPage from "./pages/pro/Videos";
import AdminDashboard from "./pages/pro/Admin";
import PayeurDashboard from "./pages/PayeurDashboard";
import BoutiquePage from "./pages/Boutique";
import EtablissementDashboard from "./pages/EtablissementDashboard";
import { homeFor } from "./lib/routes";
import { Loader2 } from "lucide-react";
import "./App.css";


function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="bc-shell flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2BA3A0]" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth/login" replace />;
  const home = homeFor(user);
  if (role === "pro" && user.role !== "therapeute") return <Navigate to={home} replace />;
  if (role === "app" && ["therapeute", "payeur", "etablissement"].includes(user.role)) return <Navigate to={home} replace />;
  if (role === "payeur" && user.role !== "payeur") return <Navigate to={home} replace />;
  if (role === "etablissement" && user.role !== "etablissement") return <Navigate to={home} replace />;
  return <>{children}<ConsentModal /></>;
}

function ProSubscription() {
  return <div className="max-w-xl"><SubscriptionPage audience="pro" /></div>;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/installer" element={<InstallGuidePage />} />
        <Route path="/relecture/:token" element={<LingalaPage />} />
        <Route path="/avis-medecin/:token" element={<AvisMedecinPage />} />
        <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
        <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
        <Route path="/app" element={<Protected role="app"><Layout /></Protected>}>
          <Route index element={<HomePage />} />
          <Route path="exercises" element={<ExercisesPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="communicate" element={<CommunicatePage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="avc" element={<AvcExplainedPage />} />
          <Route path="carnet" element={<CaregiverNotebookPage />} />
          <Route path="famille" element={<FamilyBoardPage />} />
          <Route path="alertes" element={<CaregiverAlertsPage />} />
          <Route path="urgence" element={<WorryModePage />} />
        </Route>
        <Route path="/pro" element={<Protected role="pro"><ProLayout /></Protected>}>
          <Route index element={<ProDashboard />} />
          <Route path="patients/:id" element={<PatientDetail />} />
          <Route path="patients/:id/jeu" element={<PatientJeu />} />
          <Route path="jeu" element={<JeuPage />} />
          <Route path="etude" element={<EtudePage />} />
          <Route path="etude/:pid" element={<EtudeParticipant />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="training" element={<TrainingPage />} />
          <Route path="subscription" element={<ProSubscription />} />
          <Route path="settings" element={<ProSettings />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="transcriptions" element={<TranscriptionsPage />} />
          <Route path="lingala" element={<LingalaPage />} />
          <Route path="reseau" element={<NetworkPage />} />
          <Route path="bibliotheque" element={<LibraryPage />} />
          <Route path="transmission" element={<TransmissionPage />} />
          <Route path="palabre" element={<PalabrePage />} />
          <Route path="inquietudes" element={<WorriesPage />} />
          <Route path="telesoin" element={<TelesoinPage />} />
          <Route path="nomenclature" element={<NomenclaturePage />} />
          <Route path="pays" element={<PaysDashboardPage />} />
          <Route path="clinic" element={<ClinicPage />} />
        </Route>
        <Route path="/payeur" element={<Protected role="payeur"><PayeurDashboard /></Protected>} />
        <Route path="/boutique" element={<Protected><BoutiquePage /></Protected>} />
        <Route path="/etablissement" element={<Protected role="etablissement"><EtablissementDashboard /></Protected>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <Toaster richColors position="top-center" />
        <AppRoutes />
      </AppProvider>
    </AuthProvider>
  );
}
